/**
 * Lift2Fit — the AI plan wizard.
 *
 * Six short steps: what we are training for · goal · about you · availability ·
 * equipment · injuries. Finish generates the plan (POST /programme/one-time) and
 * lands on the result screen. One primary action per step — the orange button.
 *
 * A generated plan is included in the free trial and in the £3.99/month
 * subscription; it is never a separate one-off purchase.
 */
import { useMemo, useState } from 'react';
import { api, type PlanIntake, type TrainingTrack } from '@/lib/api';
import { cn } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { Button } from '@/components/ui';
import { ArrowRightIcon, CheckIcon, MinusIcon, SparkIcon } from '@/lib/icons';
import { LiveMessage, Panel, PersonaBadge, ScreenHeader, StatusChip, TrackIcon } from '../components';
import { GOAL_OPTIONS, TRACKS, defaultGoalForTrack, trackMeta } from '../demoData';
import { saveGeneratedPlan } from '../hooks';
import { navigate } from '../router';

const EQUIPMENT: Array<{ id: PlanIntake['equipment'][number]; label: string; hint: string }> = [
  { id: 'barbell', label: 'Barbell', hint: 'Squat rack, bench, platform' },
  { id: 'dumbbell', label: 'Dumbbells', hint: 'Fixed or adjustable' },
  { id: 'machine', label: 'Machines', hint: 'Leg press, chest press, ergs' },
  { id: 'cable', label: 'Cables', hint: 'Pulley stack, rows, pressdowns' },
  { id: 'kettlebell', label: 'Kettlebells', hint: 'Swings, cleans, carries' },
  { id: 'band', label: 'Bands & mat', hint: 'Mobility and stability work' },
  { id: 'bodyweight', label: 'Bodyweight only', hint: 'No equipment needed' },
];

const INJURY_FLAGS = [
  'lower_back',
  'shoulder',
  'knee',
  'hip',
  'elbow',
  'wrist',
  'ankle',
  'neck',
];

const SEX_OPTIONS: Array<{ id: PlanIntake['sex']; label: string }> = [
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
  { id: 'unspecified', label: 'Prefer not to say' },
];

const STEP_TITLES = ['Track', 'Goal', 'About you', 'Availability', 'Equipment', 'Injuries'];

const GENERATING_MESSAGES = [
  'Reading your intake…',
  'Applying volume landmarks for your experience…',
  'Checking the week against your injury flags…',
  'Writing the block…',
];

function NumberField({
  label,
  unit,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  unit: string;
  value: number | '';
  min: number;
  max: number;
  onChange: (value: number | '') => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
        {label} <span className="text-ink-muted/70">({unit})</span>
      </span>
      <input
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next === '' ? '' : Number(next));
        }}
        className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
      />
    </label>
  );
}

export function PlanWizard() {
  const session = useSession();
  const [step, setStep] = useState(0);
  const [track, setTrack] = useState<TrainingTrack>('general');
  const [goal, setGoal] = useState<PlanIntake['goal']>('general');
  const [goalTouched, setGoalTouched] = useState(false);
  const [age, setAge] = useState<number | ''>(34);
  const [sex, setSex] = useState<PlanIntake['sex']>('unspecified');
  const [heightCm, setHeightCm] = useState<number | ''>(session.user?.heightCm ?? 178);
  const [bodyweightKg, setBodyweightKg] = useState<number | ''>(session.user?.bodyweightKg ?? 82.4);
  const [experience, setExperience] = useState<PlanIntake['experience']>(session.user?.experience ?? 'intermediate');
  const [daysPerWeek, setDaysPerWeek] = useState<PlanIntake['daysPerWeek']>(4);
  const [sessionMinutes, setSessionMinutes] = useState<PlanIntake['sessionMinutes']>(60);
  const [equipment, setEquipment] = useState<Array<PlanIntake['equipment'][number]>>(['barbell', 'dumbbell', 'machine', 'cable']);
  const [injuryFlags, setInjuryFlags] = useState<string[]>(session.user?.injuryFlags ?? []);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState(GENERATING_MESSAGES[0]!);

  const meta = trackMeta(track);
  const progress = Math.round(((step + 1) / STEP_TITLES.length) * 100);

  const equipmentToggle = (id: PlanIntake['equipment'][number]) => {
    setEquipment((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const validate = (): string | null => {
    if (step === 1 && !goal) return 'Pick the goal that matters most right now.';
    if (step === 2) {
      if (age === '' || age < 14 || age > 90) return 'Age should be between 14 and 90.';
      if (heightCm === '' || heightCm < 120 || heightCm > 230) return 'Height should be between 120 cm and 230 cm.';
      if (bodyweightKg === '' || bodyweightKg < 35 || bodyweightKg > 250) return 'Bodyweight should be between 35 kg and 250 kg.';
    }
    if (step === 4 && equipment.length === 0) return 'Pick at least one thing you can train with.';
    return null;
  };

  const next = () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setStep((value) => Math.min(STEP_TITLES.length - 1, value + 1));
  };

  const back = () => {
    setError(null);
    setStep((value) => Math.max(0, value - 1));
  };

  const intake: PlanIntake = useMemo(
    () => ({
      goal,
      track,
      experience,
      daysPerWeek,
      sessionMinutes,
      heightCm: Number(heightCm) || 0,
      bodyweightKg: Number(bodyweightKg) || 0,
      age: Number(age) || 0,
      sex,
      equipment,
      injuryFlags,
      notes: notes || undefined,
    }),
    [goal, track, experience, daysPerWeek, sessionMinutes, heightCm, bodyweightKg, age, sex, equipment, injuryFlags, notes],
  );

  const generate = async () => {
    setGenerating(true);
    setError(null);
    let index = 0;
    const ticker = window.setInterval(() => {
      index = Math.min(GENERATING_MESSAGES.length - 1, index + 1);
      setGeneratingMessage(GENERATING_MESSAGES[index]!);
    }, 420);

    const result = await api.generatePlan(intake);
    window.clearInterval(ticker);
    saveGeneratedPlan(result.data);
    navigate(`/plan/${result.data.planId}`);
  };

  if (generating) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 text-center" role="status" aria-live="polite">
        <img src="/brand/lift2fit-spinner.svg" alt="" className="l2f-spinner" />
        <h1 className="font-display text-2xl font-black uppercase text-ink">Building your {meta.label.toLowerCase()} plan</h1>
        <p className="max-w-[52ch] text-[15px] text-ink-muted">{generatingMessage}</p>
        <p className="text-[12px] uppercase tracking-[0.08em] text-ink-muted">
          Free trial or £3.99/month · no separate purchase · this usually takes a few seconds
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        eyebrow="AI plan"
        title="Build my plan"
        badge={<StatusChip tone="accent">Free trial · £3.99/month</StatusChip>}
        lead="Six short questions. Nothing here is a spreadsheet: pick the track, answer, and the coach writes the block."
        actions={<PersonaBadge track={track} />}
      />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <ol className="flex flex-wrap gap-x-4 gap-y-1" aria-label="Plan steps">
            {STEP_TITLES.map((title, index) => (
              <li
                key={title}
                aria-current={index === step ? 'step' : undefined}
                className={cn(
                  'text-[11px] font-bold uppercase tracking-[0.08em]',
                  index === step ? 'text-accent' : index < step ? 'text-ink' : 'text-ink-muted',
                )}
              >
                {index + 1}. {title}
              </li>
            ))}
          </ol>
          <span className="l2f-tabular font-mono text-[12px] text-ink-muted">
            Step {step + 1} of {STEP_TITLES.length}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-pill bg-page-subtle" role="presentation">
          <div className="h-full rounded-pill bg-accent transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <Panel
        title={step === 0 ? 'What are we training for?' : STEP_TITLES[step]}
        subtitle={
          step === 0
            ? 'Pick one. It changes the metrics, the coach persona and the whole block.'
            : step === 5
              ? 'Anything the coach must work around. This changes substitutions, never the goal.'
              : 'Short answers are fine — you can change them later.'
        }
      >
        {/* 0 — track */}
        {step === 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {TRACKS.map((option) => {
              const active = option.id === track;
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setTrack(option.id);
                    if (!goalTouched) setGoal(defaultGoalForTrack(option.id));
                  }}
                  className={cn(
                    'flex flex-col items-start gap-2 border bg-surface p-4 text-left transition-colors duration-150',
                    active ? 'border-ink' : 'border-hairline hover:border-accent',
                  )}
                >
                  <span className="flex w-full items-center justify-between gap-2">
                    <TrackIcon track={option.id} className="text-[20px] text-accent" />
                    {active ? <CheckIcon className="text-success" /> : null}
                  </span>
                  <span className="text-[15px] font-bold text-ink">{option.label}</span>
                  <span className="text-[13px] text-ink-muted">{option.description}</span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                    {option.persona}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}

        {/* 1 — goal */}
        {step === 1 ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="sr-only">Goal</legend>
            {GOAL_OPTIONS.map((option) => {
              const active = option.id === goal;
              return (
                <label
                  key={option.id}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 border bg-surface p-4',
                    active ? 'border-ink' : 'border-hairline hover:border-accent',
                  )}
                >
                  <input
                    type="radio"
                    name="goal"
                    value={option.id}
                    checked={active}
                    onChange={() => {
                      setGoal(option.id);
                      setGoalTouched(true);
                    }}
                    className="mt-1 accent-[color:var(--l2f-accent)]"
                  />
                  <span className="flex flex-col">
                    <span className="text-[15px] font-bold text-ink">{option.label}</span>
                    <span className="text-[13px] text-ink-muted">{option.description}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>
        ) : null}

        {/* 2 — about you */}
        {step === 2 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField label="Age" unit="years" value={age} min={14} max={90} onChange={setAge} />
            <NumberField label="Height" unit="cm" value={heightCm} min={120} max={230} onChange={setHeightCm} />
            <NumberField label="Bodyweight" unit="kg" value={bodyweightKg} min={35} max={250} onChange={setBodyweightKg} />
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Sex</legend>
              <div className="flex flex-wrap gap-2">
                {SEX_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={sex === option.id}
                    onClick={() => setSex(option.id)}
                    className={cn(
                      'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                      sex === option.id ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="text-[12px] text-ink-muted">Used only to set sensible defaults. Canonical units stay metric.</p>
            </fieldset>
          </div>
        ) : null}

        {/* 3 — availability */}
        {step === 3 ? (
          <div className="flex flex-col gap-5">
            <fieldset className="flex flex-col gap-2">
              <legend className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Days per week</legend>
              <div className="flex flex-wrap gap-2">
                {([2, 3, 4, 5, 6] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={daysPerWeek === option}
                    onClick={() => setDaysPerWeek(option)}
                    className={cn(
                      'h-11 w-14 border text-[14px] font-bold',
                      daysPerWeek === option ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Session length</legend>
              <div className="flex flex-wrap gap-2">
                {([30, 45, 60, 75, 90] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={sessionMinutes === option}
                    onClick={() => setSessionMinutes(option)}
                    className={cn(
                      'h-11 border px-4 text-[13px] font-bold',
                      sessionMinutes === option ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
                    )}
                  >
                    {option} min
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Experience</legend>
              <div className="flex flex-wrap gap-2">
                {(['beginner', 'intermediate', 'advanced'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={experience === option}
                    onClick={() => setExperience(option)}
                    className={cn(
                      'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                      experience === option ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </fieldset>
          </div>
        ) : null}

        {/* 4 — equipment */}
        {step === 4 ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="sr-only">Equipment</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {EQUIPMENT.map((option) => {
                const active = equipment.includes(option.id);
                return (
                  <label
                    key={option.id}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 border bg-surface p-3.5',
                      active ? 'border-ink' : 'border-hairline hover:border-accent',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => equipmentToggle(option.id)}
                      className="mt-1 accent-[color:var(--l2f-accent)]"
                    />
                    <span className="flex flex-col">
                      <span className="text-[14px] font-bold text-ink">{option.label}</span>
                      <span className="text-[12px] text-ink-muted">{option.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ) : null}

        {/* 5 — injuries */}
        {step === 5 ? (
          <div className="flex flex-col gap-4">
            <fieldset className="flex flex-col gap-2">
              <legend className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                Anything we must work around?
              </legend>
              <div className="flex flex-wrap gap-2">
                {INJURY_FLAGS.map((flag) => {
                  const active = injuryFlags.includes(flag);
                  return (
                    <button
                      key={flag}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        setInjuryFlags((prev) => (prev.includes(flag) ? prev.filter((value) => value !== flag) : [...prev, flag]))
                      }
                      className={cn(
                        'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.06em]',
                        active ? 'border-danger text-danger' : 'border-hairline bg-surface text-ink hover:border-accent',
                      )}
                    >
                      {flag.replace(/_/g, ' ')}
                    </button>
                  );
                })}
              </div>
              <p className="text-[12px] text-ink-muted">
                No flags selected means no restrictions. Pain at 4/10 or above always changes the plan, whatever the goal.
              </p>
            </fieldset>

            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Notes for the coach (optional)</span>
              <textarea
                rows={3}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={
                  track === 'wellness'
                    ? 'e.g. desk job, stiff hips in the morning, cannot kneel'
                    : 'e.g. train at home on Tuesdays, ergs only on the weekend'
                }
                className="border border-hairline bg-page p-3 text-[14px] text-ink"
              />
            </label>

            <div className="flex flex-col gap-2 border border-hairline bg-page-subtle p-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Included in your plan</span>
              <p className="text-[14px] text-ink">
                A generated plan is part of the <span className="font-bold">free trial</span> or your{' '}
                <span className="font-bold">£3.99/month</span> subscription. It is saved to your account and stays
                yours — there is no separate one-off purchase.
              </p>
              <ul className="flex flex-col gap-1 text-[13px] text-ink-muted">
                <li>
                  <CheckIcon className="mr-1.5 inline text-success" />
                  {meta.label} block · {daysPerWeek} days per week · {sessionMinutes} min sessions
                </li>
                <li>
                  <CheckIcon className="mr-1.5 inline text-success" />
                  {meta.persona} voice, matched to your log
                </li>
                <li>
                  <CheckIcon className="mr-1.5 inline text-success" />
                  Injury-aware substitutions and a deload every 4th week
                </li>
              </ul>
            </div>
          </div>
        ) : null}

        <LiveMessage message={error} tone="danger" />
      </Panel>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={back} disabled={step === 0}>
          <MinusIcon />
          Back
        </Button>
        {step < STEP_TITLES.length - 1 ? (
          <Button size="lg" onClick={next}>
            Continue
            <ArrowRightIcon />
          </Button>
        ) : (
          <Button size="lg" onClick={generate} disabled={generating}>
            Generate my plan
            <SparkIcon />
          </Button>
        )}
      </div>

      {session.isDemo ? (
        <p className="text-[12px] text-ink-muted">
          Demo data — start the API to use your own. In demo mode the generator returns a faithful sample block for the track you
          chose.
        </p>
      ) : null}
    </div>
  );
}
